import { createAsyncThunk } from '@reduxjs/toolkit';

import { navigationRef } from '@helpers';
import { TAG_KEYS } from '@components/imageHandling/common';
import { getDocumentById } from '@reducers/search';
import { refreshMachineImagesOnUpload } from '@reducers/machines';
import {
  addOrUpdateImageDocument,
  removeImageDocumentFromSpecificContext,
} from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { parseAndAddUrlsToImagesDocuments } from '@reducers/v2/imageHandlingV2/thunks/parseAndAddUrlsToImagesDocuments';
import { TTL_FOR_IMAGE } from '@v2/components/imageHandling/constants';
import type { AppDispatch } from '@store/types';
import type { ImageContext, ODataDocument } from '@v2/components/imageHandling/types/types';
import type { Machine } from '@reducers/machines/types';

interface PendingLocalUploadContext {
  tempID?: string;
  imageContexts?: ImageContext[];
  isSaveCropImageAsNewImageFlow?: boolean;
}

const pendingLocalUploads = new Map<string, PendingLocalUploadContext>();
const pendingEvictionTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const registerPendingLocalUpload = (documentBlobId: string, ctx: PendingLocalUploadContext) => {
  pendingLocalUploads.set(documentBlobId, ctx);
  const previousTimer = pendingEvictionTimers.get(documentBlobId);
  if (previousTimer) clearTimeout(previousTimer);
  const timer = setTimeout(() => {
    pendingLocalUploads.delete(documentBlobId);
    pendingEvictionTimers.delete(documentBlobId);
  }, TTL_FOR_IMAGE);
  pendingEvictionTimers.set(documentBlobId, timer);
};

const consumePendingLocalUpload = (documentBlobId: string): PendingLocalUploadContext | undefined => {
  const ctx = pendingLocalUploads.get(documentBlobId);
  pendingLocalUploads.delete(documentBlobId);
  clearTimeout(pendingEvictionTimers.get(documentBlobId));
  pendingEvictionTimers.delete(documentBlobId);
  return ctx;
};

export interface UploadImageDocumentMessageHandlerPayload {
  documentBlobId: string;
  documentId?: number;
  image?: ODataDocument;
}

export const uploadImageDocumentMessageHandler = createAsyncThunk<void, UploadImageDocumentMessageHandlerPayload, { dispatch: AppDispatch }>(
  'imageHandlingV2/uploadImageDocumentMessageHandler',
  async ({ documentBlobId, documentId, image: eventImage }, { dispatch }) => {
    const resolvedDocumentId = documentId ?? eventImage?.documentId;
    if (!resolvedDocumentId) return;

    const documentData = await dispatch(getDocumentById(resolvedDocumentId));
    if (!documentData?.length) return;

    const image = eventImage ? { ...eventImage, ...documentData[0] } : documentData[0];
    if (!image) return;

    const parsedResult = await dispatch(parseAndAddUrlsToImagesDocuments({ imagesDocuments: [image] }));
    if (!parseAndAddUrlsToImagesDocuments.fulfilled.match(parsedResult)) return;

    const parsedData = parsedResult.payload?.[0];
    if (!parsedData) return;

    const pending = consumePendingLocalUpload(documentBlobId);
    const { tempID, imageContexts, isSaveCropImageAsNewImageFlow } = pending ?? {};

    const fallbackContexts: ImageContext[] = (eventImage?.links ?? [])
      .filter((link) => link?.scopeId != null && link?.scopeReferenceId != null)
      .map((link) => ({
        scopeId: link.scopeId as keyof typeof TAG_KEYS,
        scopeReferenceId: link.scopeReferenceId as number,
      }));

    const contextsToUpdate: ImageContext[] = imageContexts?.length
      ? imageContexts
      : fallbackContexts.length
        ? fallbackContexts
        : [{ documentId: resolvedDocumentId }];

    contextsToUpdate.forEach((imageContext) => {
      dispatch(addOrUpdateImageDocument({ document: parsedData, imageContext, replacementDocumentId: tempID }));
    });

    if (tempID && isSaveCropImageAsNewImageFlow) {
      dispatch(addOrUpdateImageDocument({ document: parsedData, imageContext: { documentId: resolvedDocumentId } }));

      const currentRoute = navigationRef?.current?.getCurrentRoute();
      const documentIdFromParams = (currentRoute?.params as { documentId?: string } | undefined)?.documentId;
      if (documentIdFromParams === tempID) {
        navigationRef?.current?.setParams({ documentId: resolvedDocumentId });
      }

      dispatch(removeImageDocumentFromSpecificContext({ documentId: tempID, imageContext: { documentId: tempID } }));
    }

    if (parsedData.links?.length) {
      dispatch(
        refreshMachineImagesOnUpload({
          links: parsedData.links,
          machineImage: parsedData as NonNullable<Machine['machineImage']>,
        })
      );
    }
  }
);
